import { getScrollContainer as getScrollContainerFromScrollUtils } from './scrollUtils';

/** 复用 scrollUtils 的滚动容器解析，避免两处维护不一致 */
export const getScrollContainer = getScrollContainerFromScrollUtils;

const DEFAULT_MARGIN = 24;

/**
 * 判断元素是否（完全或部分）在容器可视区域外（纵向）。
 * 用于“部分被遮挡也需滚动”的场景，确保整块高亮移入视口。
 */
function isPartiallyOutsideVertical(elRect: DOMRect, containerRect: DOMRect): boolean {
    return elRect.top < containerRect.top || elRect.bottom > containerRect.bottom;
}

/**
 * 判断元素是否（完全或部分）在容器可视区域外（横向）。
 * 用于“部分被遮挡也需滚动”的场景，确保整块高亮移入视口。
 */
function isPartiallyOutsideHorizontal(elRect: DOMRect, containerRect: DOMRect): boolean {
    return elRect.left < containerRect.left || elRect.right > containerRect.right;
}

/**
 * 计算横向滚动量 deltaX，使高亮在视口内且尽量左右都不被裁。
 * 同时考虑左边界和右边界：若可行则取 [deltaXMin, deltaXMax] 中点；元素宽于视口时则保左侧可见。
 */
function computeDeltaX(
    elRect: DOMRect,
    viewportRect: DOMRect,
    margin: number,
): number {
    const rightOut = elRect.right > viewportRect.right - margin;
    const leftOut = elRect.left < viewportRect.left + margin;
    const deltaXMin = rightOut ? elRect.right - (viewportRect.right - margin) : -Infinity;
    const deltaXMax = leftOut ? elRect.left - (viewportRect.left + margin) : Infinity;
    if (!rightOut && !leftOut) return 0;
    if (deltaXMin > deltaXMax) {
        return deltaXMax;
    }
    if (Number.isFinite(deltaXMin) && Number.isFinite(deltaXMax)) {
        return (deltaXMin + deltaXMax) / 2;
    }
    return Number.isFinite(deltaXMin) ? deltaXMin : deltaXMax;
}

/**
 * 将 scrollTop/scrollLeft 限制在有效范围内。
 */
function clampScroll(value: number, min: number, max: number): number {
    if (!Number.isFinite(value)) return min;
    if (value < min) return min;
    if (value > max) return max;
    return value;
}

/**
 * 获取滚动容器的“可视视口”矩形（视口坐标）。
 * 若当前容器无溢出则沿父节点向上找第一个有溢出的祖先；若整链均无溢出或得到的矩形明显大于一屏，
 * 则回退为窗口视口（避免非全屏下 .pdf-content-wrap 等被内容撑满导致误判“已在视口内”）。
 */
function getVisibleViewportRect(scrollContainer: HTMLElement): DOMRect {
    let rect: DOMRect | undefined;
    let el: HTMLElement | null = scrollContainer;
    while (el) {
        const hasOverflowY = el.scrollHeight > el.clientHeight;
        const hasOverflowX = el.scrollWidth > el.clientWidth;
        if (hasOverflowY || hasOverflowX) {
            const r = el.getBoundingClientRect();
            rect = new DOMRect(r.left, r.top, el.clientWidth, el.clientHeight);
            break;
        }
        el = el.parentElement;
    }
    if (rect === undefined) {
        const r = scrollContainer.getBoundingClientRect();
        rect = new DOMRect(
            r.left,
            r.top,
            scrollContainer.clientWidth,
            scrollContainer.clientHeight,
        );
    }
    const win =
        typeof window !== 'undefined'
            ? { w: window.innerWidth, h: window.innerHeight }
            : { w: rect.width, h: rect.height };
    if (rect.height > win.h * 1.5 || rect.width > win.w * 1.5) {
        rect = new DOMRect(0, 0, win.w, win.h);
    }
    return rect;
}

/**
 * 从元素向上查找第一个具有横向滚动空间的祖先（用于全屏时纵向在 doc-container、横向在 .pdf-content-wrap 等场景）。
 */
function findHorizontalScrollContainer(element: HTMLElement): HTMLElement | null {
    let el: HTMLElement | null = element.parentElement;
    while (el) {
        if (el.scrollWidth > el.clientWidth) return el;
        el = el.parentElement;
    }
    return null;
}

/** 可视区域预留（如非全屏下 fixed 底栏/侧栏占用，避免高亮被遮挡） */
interface EnsureVisibleOptions {
    /** 底部预留像素（如 SiblingsNav 等 fixed 底栏高度），非全屏可传 54 */
    reservedBottom?: number;
    /** 左侧预留像素 */
    reservedLeft?: number;
    /** 右侧预留像素 */
    reservedRight?: number;
}

/**
 * 确保目标元素在滚动容器的可视区域内（纵向 + 横向）。
 * 支持 reservedBottom/reservedLeft/reservedRight 扣除固定栏占位，避免被遮挡；
 * 采用“部分在视口外即滚动”，确保整块高亮完全移入可视区域。
 * 无副作用：仅调用各容器的 scrollTo / window.scrollTo，不修改其他 DOM 或全局状态。
 *
 * @param scrollContainer - 主滚动容器（由 getScrollContainer 得到，用于纵向及默认横向）
 * @param element - 需要滚入视口的元素（如 .pdf-content-text-search-highlight.selected）
 * @param margin - 视口边缘安全边距（默认 24）
 * @param options - 可选，reservedBottom 等预留像素（非全屏底栏高度建议 54）
 */
export function ensureElementVisible(
    scrollContainer: HTMLElement,
    element: HTMLElement,
    margin: number = DEFAULT_MARGIN,
    options: EnsureVisibleOptions = {},
): void {
    let viewportRect = getVisibleViewportRect(scrollContainer);
    const rb = options.reservedBottom ?? 0;
    const rl = options.reservedLeft ?? 0;
    const rr = options.reservedRight ?? 0;
    if (rb > 0 || rl > 0 || rr > 0) {
        viewportRect = new DOMRect(
            viewportRect.left + rl,
            viewportRect.top,
            Math.max(0, viewportRect.width - rl - rr),
            Math.max(0, viewportRect.height - rb),
        );
    }
    const elRect = element.getBoundingClientRect();

    const needScrollY = isPartiallyOutsideVertical(elRect, viewportRect);
    const needScrollX = isPartiallyOutsideHorizontal(elRect, viewportRect);
    if (!needScrollY && !needScrollX) return;

    let deltaY = 0;
    if (needScrollY) {
        if (elRect.top < viewportRect.top) {
            deltaY = elRect.top - (viewportRect.top + margin);
        } else {
            deltaY = elRect.bottom - (viewportRect.bottom - margin);
        }
    }

    // 纵向：优先用主滚动容器；若其无纵向滚动空间（maxScrollTop=0）则回退为 window 滚动（非全屏整页滚动场景）
    if (needScrollY) {
        const maxScrollTop = Math.max(0, scrollContainer.scrollHeight - scrollContainer.clientHeight);
        const nextTop = clampScroll(scrollContainer.scrollTop + deltaY, 0, maxScrollTop);
        if (maxScrollTop > 0) {
            scrollContainer.scrollTo({
                top: nextTop,
                left: scrollContainer.scrollLeft,
                behavior: 'auto',
            });
        } else if (typeof window !== 'undefined') {
            const windowNextY = Math.max(0, window.scrollY + deltaY);
            const maxWindowScrollY = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
            window.scrollTo({ top: Math.min(windowNextY, maxWindowScrollY), left: window.scrollX, behavior: 'auto' });
        }
    }

    // 横向：若主容器有横向滚动空间则用主容器，否则用祖先中具有横向滚动的节点；若仍无空间则回退为 window 滚动
    const horizontalContainer =
        scrollContainer.scrollWidth > scrollContainer.clientWidth
            ? scrollContainer
            : findHorizontalScrollContainer(element);
    if (needScrollX) {
        let deltaXForWindow = 0;
        let didScrollContainerX = false;
        if (horizontalContainer) {
            let viewportRectX = getVisibleViewportRect(horizontalContainer);
            if (rl > 0 || rr > 0) {
                viewportRectX = new DOMRect(
                    viewportRectX.left + rl,
                    viewportRectX.top,
                    Math.max(0, viewportRectX.width - rl - rr),
                    viewportRectX.height,
                );
            }
            const elRectX = element.getBoundingClientRect();
            const outsideX = isPartiallyOutsideHorizontal(elRectX, viewportRectX);
            if (outsideX) {
                const deltaXInner = computeDeltaX(elRectX, viewportRectX, margin);
                deltaXForWindow = deltaXInner;
                const maxScrollLeft = Math.max(0, horizontalContainer.scrollWidth - horizontalContainer.clientWidth);
                const nextLeft = clampScroll(horizontalContainer.scrollLeft + deltaXInner, 0, maxScrollLeft);
                if (maxScrollLeft > 0) {
                    horizontalContainer.scrollTo({
                        top: horizontalContainer.scrollTop,
                        left: nextLeft,
                        behavior: 'auto',
                    });
                    didScrollContainerX = true;
                }
            }
        } else {
            const elRectX = element.getBoundingClientRect();
            deltaXForWindow = computeDeltaX(elRectX, viewportRect, margin);
        }
        if (!didScrollContainerX && deltaXForWindow !== 0 && typeof window !== 'undefined') {
            const maxScrollLeftWin = Math.max(0, document.documentElement.scrollWidth - window.innerWidth);
            const windowNextX = clampScroll(window.scrollX + deltaXForWindow, 0, maxScrollLeftWin);
            if (windowNextX !== window.scrollX) {
                window.scrollTo({ top: window.scrollY, left: windowNextX, behavior: 'auto' });
            }
        }
    }
}
